import { useCallback, useEffect, useState } from 'react';
import type { TourChapterId, TourTrackId } from './tour-chapters';

export type TourLanguage = 'en' | 'ur';

export const TOUR_LANGUAGE_KEY = 'his.landing.tourLanguage';

export const LANGUAGE_OPTIONS: readonly { id: TourLanguage; label: string }[] = [
  { id: 'en', label: 'English' },
  { id: 'ur', label: 'Roman Urdu' },
];

interface Labelled {
  label: string;
  blurb: string;
}

export interface TourText {
  eyebrow: string;
  title: string;
  lead: string;
  previous: string;
  next: string;
  pause: string;
  play: string;
  replay: string;
  pauseLabel: string;
  playLabel: string;
  replayLabel: string;
  goTo: string;
  languageLabel: string;
  fullTour: string;
  tracksLabel: string;
  tabsLabel: string;
  stepsLabel: string;
  nextChapter: string;
  tracks: Record<TourTrackId, Labelled>;
  chapters: Record<TourChapterId, Labelled>;
}

export const TOUR_TEXT: Record<TourLanguage, TourText> = {
  en: {
    eyebrow: 'Product tour',
    title: 'From the front desk to the paid bill',
    lead: 'New to HIS? Watch the real screens fill in, step by step. Pick what you want to see - the hospital outpatient clinic, the hospital wards, a laboratory or a medical store - then follow one patient or one customer all the way through. Choose any chapter, or let it play.',
    previous: 'Previous step',
    next: 'Next step',
    pause: 'Pause',
    play: 'Play',
    replay: 'Replay',
    pauseLabel: 'Pause the tour',
    playLabel: 'Play the tour',
    replayLabel: 'Replay the tour',
    goTo: 'Go to',
    languageLabel: 'Tour language',
    fullTour: 'Full tour',
    tracksLabel: 'What would you like to see?',
    tabsLabel: 'Choose what to watch',
    stepsLabel: 'Steps in this part',
    nextChapter: 'Watch next',
    tracks: {
      opd: { label: 'Hospital: OPD', blurb: 'Register, book, consult, order tests and bill' },
      ipd: { label: 'Hospital: IPD', blurb: 'Beds, admission, ward care and discharge' },
      lab: { label: 'Laboratory', blurb: 'A stand-alone lab from walk-in to released report' },
      store: { label: 'Medical store', blurb: 'Stock, expiry and the counter, without the hospital' },
    },
    chapters: {
      'o-start': { label: 'Getting started', blurb: 'Sign in, your day, staff and settings' },
      'o-patients': { label: 'Patients', blurb: 'Search, register, open the chart' },
      'o-clinic': { label: 'Clinic', blurb: 'Hours, booking, vitals, consultation' },
      'o-billing': { label: 'Billing', blurb: 'Shift, invoices, payment, drawer' },
      'o-reports': { label: 'Reports and audit', blurb: 'Numbers and the record of every change' },
      'i-admit': { label: 'Admission', blurb: 'Bed board and admitting a patient' },
      'i-care': { label: 'Ward care', blurb: 'Orders, notes, eMAR and vitals' },
      'i-discharge': { label: 'Bill and discharge', blurb: 'Running bill, transfer, discharge' },
      'l-start': { label: 'Getting started', blurb: 'Sign in and set up your tests' },
      'l-orders': { label: 'Register & order', blurb: 'Walk-in patient, tests, price' },
      'l-bench': { label: 'Bench to report', blurb: 'Pay, collect, result, release' },
      's-start': { label: 'Getting started', blurb: 'Sign in, products, suppliers' },
      's-stock': { label: 'Stock', blurb: 'Receive goods, watch expiry' },
      's-sales': { label: 'Counter sales', blurb: 'Sell, and see stock and takings move' },
    },
  },
  ur: {
    eyebrow: 'Product tour',
    title: 'Front desk se paid bill tak',
    lead: 'HIS naya hai? Asli screens ko step by step bharte hue dekhein. Pehle chunein aap kya dekhna chahte hain - hospital ka OPD, uske wards, laboratory ya medical store - phir ek mareez ya ek customer ke saath poora safar dekhein. Koi bhi chapter chunein, ya isay khud chalne dein.',
    previous: 'Pichla step',
    next: 'Agla step',
    pause: 'Rokein',
    play: 'Chalayen',
    replay: 'Dobara',
    pauseLabel: 'Tour rokein',
    playLabel: 'Tour chalayen',
    replayLabel: 'Tour dobara chalayen',
    goTo: 'Yahan jayen',
    languageLabel: 'Tour ki zaban',
    fullTour: 'Poora tour',
    tracksLabel: 'Aap kya dekhna chahte hain?',
    tabsLabel: 'Jo dekhna hai woh chunein',
    stepsLabel: 'Is hissay ke steps',
    nextChapter: 'Agla dekhein',
    tracks: {
      opd: { label: 'Hospital: OPD', blurb: 'Register, booking, consultation, tests aur billing' },
      ipd: { label: 'Hospital: IPD', blurb: 'Beds, admission, ward ki dekh-bhal aur discharge' },
      lab: { label: 'Laboratory', blurb: 'Akeli lab: walk-in se released report tak' },
      store: { label: 'Medical store', blurb: 'Stock, expiry aur counter, hospital ke baghair' },
    },
    chapters: {
      'o-start': { label: 'Shuruaat', blurb: 'Sign in, aap ka din, staff aur settings' },
      'o-patients': { label: 'Mareez', blurb: 'Dhoondein, register karein, chart kholen' },
      'o-clinic': { label: 'Clinic', blurb: 'Timings, booking, vitals, consultation' },
      'o-billing': { label: 'Billing', blurb: 'Shift, invoice, payment, drawer' },
      'o-reports': { label: 'Reports aur audit', blurb: 'Figures aur har tabdeeli ka record' },
      'i-admit': { label: 'Admission', blurb: 'Bed board aur mareez ko admit karna' },
      'i-care': { label: 'Ward ki dekh-bhal', blurb: 'Orders, notes, eMAR aur vitals' },
      'i-discharge': { label: 'Bill aur discharge', blurb: 'Running bill, transfer, discharge' },
      'l-start': { label: 'Shuruaat', blurb: 'Sign in karein aur apne tests set karein' },
      'l-orders': { label: 'Register aur order', blurb: 'Walk-in mareez, tests, qeemat' },
      'l-bench': { label: 'Bench se report tak', blurb: 'Payment, sample, result, release' },
      's-start': { label: 'Shuruaat', blurb: 'Sign in, products, suppliers' },
      's-stock': { label: 'Stock', blurb: 'Maal receive karein, expiry dekhein' },
      's-sales': { label: 'Counter sales', blurb: 'Bechein, aur stock aur takings ka hisaab dekhein' },
    },
  },
};

function readStored(): TourLanguage {
  try {
    return window.localStorage.getItem(TOUR_LANGUAGE_KEY) === 'ur' ? 'ur' : 'en';
  } catch {
    return 'en';
  }
}

export function useTourLanguage(): [TourLanguage, (next: TourLanguage) => void] {
  const [language, setLanguage] = useState<TourLanguage>('en');

  useEffect(() => {
    setLanguage(readStored());
  }, []);

  const choose = useCallback((next: TourLanguage) => {
    setLanguage(next);
    try {
      window.localStorage.setItem(TOUR_LANGUAGE_KEY, next);
    } catch {
      return;
    }
  }, []);

  return [language, choose];
}
